import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { usePropertyPanel } from '../PropertyPanelContext';
import PaddingControl from '../CommonProperties/PaddingControl';
import BorderRadiusControl from '../CommonProperties/BorderRadiusControl';
import StyleControl from '../CommonProperties/StyleControl';

const BoardProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  
  // PropertyPanelContext에서 필요한 함수 가져오기
  const { devicePropertyTab } = usePropertyPanel();
  
  // 게시판용 디바이스 탭 상태 (PropertyPanelContext의 devicePropertyTab과 동기화)
  const [boardDeviceTab, setBoardDeviceTab] = useState(devicePropertyTab || 'pc');
  
  // devicePropertyTab이 변경될 때 boardDeviceTab도 함께 변경
  React.useEffect(() => {
    setBoardDeviceTab(devicePropertyTab);
  }, [devicePropertyTab]);
  
  // 디바이스별 데이터 변경 핸들러
  const handleBoardDeviceDataChange = (field, value) => {
    if (!selectedComponent) return;
    
    
    // 전체 데이터의 깊은 복사
    const existingData = JSON.parse(JSON.stringify(selectedComponent.data || {}));
    
    // 초기 디바이스 객체 설정
    if (!existingData.device) {
      existingData.device = { pc: {}, mobile: {} };
      
      // 기존 값이 있으면 초기 마이그레이션 (양쪽에 같은 값 설정)
      Object.keys(existingData).forEach(key => {
        // device 키와 업데이트할 필드 자체는 복사하지 않음
        if (key !== 'device' && key !== field) {
          existingData.device.pc[key] = existingData[key];
          existingData.device.mobile[key] = existingData[key];
        }
      });
    }
    
    // 현재 디바이스 객체 확인
    if (!existingData.device[boardDeviceTab]) {
      existingData.device[boardDeviceTab] = {};
    }
    
    // 현재 디바이스 데이터 업데이트 (다른 디바이스 데이터는 그대로 유지)
    existingData.device[boardDeviceTab][field] = value;
    
    // 최상위 필드도 함께 업데이트 (전역 속성 유지)
    existingData[field] = value;
    
    
    // Redux 액션으로 업데이트
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        data: existingData
      }
    }));
  };
  
  // 디바이스별 값 가져오기 함수
  const getDeviceValue = (data, field, defaultValue) => {
    // 데이터가 없으면 기본값 반환
    if (!data) {
      return defaultValue;
    }
    
    try {
      // device 객체가 있고, 현재 선택된 디바이스에 해당 필드가 있는 경우
      if (data.device && 
          data.device[boardDeviceTab] && 
          data.device[boardDeviceTab][field] !== undefined) {
        
        const value = data.device[boardDeviceTab][field];
        return value;
      }
      
      // 디바이스별 설정이 없는 경우, 최상위 필드 확인
      if (data[field] !== undefined) {
        const value = data[field];
        return value;
      }
      
      // 모든 경우에 해당 없으면 기본값 반환
      return defaultValue;
    } catch (err) {
      console.error(`getDeviceValue 오류:`, err);
      return defaultValue;
    }
  };
  
  // 체크박스 변경 핸들러
  const handleCheckboxChange = (field) => (e) => {
    handleBoardDeviceDataChange(field, e.target.checked);
  };
  
  return (
    <>
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
            <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          게시판 설정
        </div>
        
        <div className="property-control">
          <label style={{ minWidth: '30%' }}>게시판 제목</label>
          <input
            type="text"
            value={getDeviceValue(selectedComponent.data, 'title', '')}
            onChange={(e) => handleBoardDeviceDataChange('title', e.target.value)}
          />
        </div>
        
        <div className="property-control">
          <label style={{ minWidth: '30%' }}>API 파라미터</label>
          <input
            type="text"
            value={getDeviceValue(selectedComponent.data, 'parameter', '')}
            onChange={(e) => handleBoardDeviceDataChange('parameter', e.target.value)}
            placeholder="API 파라미터를 입력하세요"
          />
          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
            예: board1, board2, board3, board4 등
          </small>
        </div>
      </div>
      
      <div className="property-group">
        <div className="property-group-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 10H19M5 14H19M5 18H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 6V6.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          버튼 표시 설정
        </div>
        
        <div className="property-control" style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>새 글 작성 버튼</span>
            <div 
              style={{ 
                position: 'relative', 
                width: '44px', 
                height: '22px', 
                backgroundColor: getDeviceValue(selectedComponent.data, 'showNewButton', true) ? '#3b82f6' : '#ccc',
                borderRadius: '34px', 
                transition: '.3s',
                cursor: 'pointer'
              }}
              onClick={() => handleBoardDeviceDataChange('showNewButton', !getDeviceValue(selectedComponent.data, 'showNewButton', true))}
            >
              <div 
                style={{ 
                  position: 'absolute',
                  height: '18px',
                  width: '18px',
                  left: getDeviceValue(selectedComponent.data, 'showNewButton', true) ? '24px' : '2px',
                  bottom: '2px',
                  backgroundColor: 'white',
                  transition: '.3s',
                  borderRadius: '50%'
                }}
              />
            </div>
          </div>
        </div>
        
       
        <div className="property-control" style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>수정하기 버튼</span>
            <div 
              style={{ 
                position: 'relative', 
                width: '44px', 
                height: '22px', 
                backgroundColor: getDeviceValue(selectedComponent.data, 'showEditButton', true) ? '#3b82f6' : '#ccc',
                borderRadius: '34px', 
                transition: '.3s',
                cursor: 'pointer'
              }}
              onClick={() => handleBoardDeviceDataChange('showEditButton', !getDeviceValue(selectedComponent.data, 'showEditButton', true))}
            >
              <div 
                style={{ 
                  position: 'absolute',
                  height: '18px',
                  width: '18px',
                  left: getDeviceValue(selectedComponent.data, 'showEditButton', true) ? '24px' : '2px',
                  bottom: '2px',
                  backgroundColor: 'white',
                  transition: '.3s',
                  borderRadius: '50%'
                }}
              />
            </div>
          </div>
        </div>
        
        <div className="property-control" style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>삭제하기 버튼</span>
            <div 
              style={{ 
                position: 'relative', 
                width: '44px', 
                height: '22px', 
                backgroundColor: getDeviceValue(selectedComponent.data, 'showDeleteButton', true) ? '#3b82f6' : '#ccc',
                borderRadius: '34px', 
                transition: '.3s',
                cursor: 'pointer'
              }}
              onClick={() => handleBoardDeviceDataChange('showDeleteButton', !getDeviceValue(selectedComponent.data, 'showDeleteButton', true))}
            >
              <div 
                style={{ 
                  position: 'absolute',
                  height: '18px',
                  width: '18px',
                  left: getDeviceValue(selectedComponent.data, 'showDeleteButton', true) ? '24px' : '2px',
                  bottom: '2px',
                  backgroundColor: 'white',
                  transition: '.3s',
                  borderRadius: '50%'
                }}
              />
            </div>
          </div>
        </div>
        
        <div className="property-control" style={{ marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>댓글 작성 버튼</span>
            <div 
              style={{ 
                position: 'relative', 
                width: '44px', 
                height: '22px', 
                backgroundColor: getDeviceValue(selectedComponent.data, 'showCommentButton', true) ? '#3b82f6' : '#ccc',
                borderRadius: '34px', 
                transition: '.3s',
                cursor: 'pointer'
              }}
              onClick={() => handleBoardDeviceDataChange('showCommentButton', !getDeviceValue(selectedComponent.data, 'showCommentButton', true))}
            >
              <div 
                style={{ 
                  position: 'absolute',
                  height: '18px',
                  width: '18px',
                  left: getDeviceValue(selectedComponent.data, 'showCommentButton', true) ? '24px' : '2px',
                  bottom: '2px',
                  backgroundColor: 'white',
                  transition: '.3s',
                  borderRadius: '50%'
                }}
              />
            </div>
          </div>
        </div>
      </div>
      
      {/* 공통 속성 컴포넌트 추가 */}
      <PaddingControl />
      <BorderRadiusControl />
      
      <StyleControl />
    </>
  );
};

export default BoardProperties; 